import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, CalendarClock, Bell, Clock, Loader2, Package } from "lucide-react";

function hoursLeft(deadline) {
  if (!deadline) return null;
  return Math.max(0, (new Date(deadline) - new Date()) / 36e5);
}

export default function AdminOrderAvailability() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadAll = async () => {
    const [o, a] = await Promise.all([
      api.get("/admin/orders/pending-availability"),
      api.get("/admin/stock-alerts"),
    ]);
    setOrders(o.data);
    setAlerts(a.data);
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    loadAll().catch(() => toast.error("Erreur chargement")).finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/connexion"/>;
  if (user.role !== "admin") return <Navigate to="/"/>;

  const set = async (oid, payload, successMsg) => {
    try {
      await api.post(`/admin/orders/${oid}/availability`, payload);
      toast.success(successMsg);
      setSelected(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const notifyProduct = async (pid) => {
    try {
      const { data } = await api.post(`/admin/products/${pid}/notify-stock-alerts`);
      toast.success(`${data.sent} email(s) envoyé(s)`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-availability-page">
      <Button variant="ghost" onClick={() => nav("/admin")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1"/> Retour au tableau de bord</Button>
      <h1 className="heading font-extrabold text-3xl text-[#111812] mb-2">Confirmation de disponibilité</h1>
      <p className="text-[#4B5563] mb-6">Vérifiez la disponibilité des produits commandés dans les 6h. Passé ce délai, la commande passe automatiquement en "épuisé".</p>

      {loading ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div> : (
        <>
          <Card className="mb-6">
            <CardHeader><CardTitle>⏳ Commandes en attente ({orders.length})</CardTitle></CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <p className="text-sm text-[#4B5563] py-4 text-center">Aucune commande en attente. Bravo 🎉</p>
              ) : (
                <div className="space-y-3">
                  {orders.map((o) => {
                    const h = hoursLeft(o.availability_deadline);
                    const urgent = h != null && h < 1;
                    return (
                      <div key={o.id} className={`border rounded-lg p-4 ${urgent ? "border-red-300 bg-red-50" : "border-[#1A3322]/10"}`} data-testid={`pending-order-${o.id}`}>
                        <div className="flex justify-between flex-wrap gap-3">
                          <div className="flex-1 min-w-[200px]">
                            <p className="font-mono font-bold text-sm">{o.reference || `#${o.id.slice(0,8)}`}</p>
                            <p className="text-xs text-[#4B5563]">{new Date(o.created_at).toLocaleString("fr-FR")}</p>
                            <p className="text-sm mt-1"><strong>{o.total?.toFixed(2)} €</strong> · {o.payment_status} · {o.provider || "stripe"}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${urgent ? "bg-red-200 text-red-900" : "bg-orange-100 text-orange-900"}`}>
                              <Clock className="w-3 h-3"/>
                              {h > 1 ? `${h.toFixed(1)}h restantes` : h > 0 ? `${Math.round(h * 60)} min` : "Expiration imminente"}
                            </div>
                          </div>
                        </div>
                        <ul className="text-sm mt-2 space-y-0.5">
                          {(o.items || []).map((it, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <Package className="w-3 h-3 text-[#4B5563]"/>
                              <span>{it.title}</span>
                              <span className="text-xs text-[#4B5563]">× {it.quantity}</span>
                            </li>
                          ))}
                        </ul>
                        {selected === o.id ? (
                          <DelayedForm order={o} onCancel={() => setSelected(null)} onSubmit={(days, date, note) => set(o.id, { status: "delayed", expected_available_days: days, expected_available_date: date, note }, "Commande marquée disponible plus tard")}/>
                        ) : (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            <Button size="sm" onClick={() => set(o.id, { status: "available", note: "" }, "Marquée disponible")} className="pill-btn bg-green-600 hover:bg-green-700 rounded-full h-9 text-xs font-bold" data-testid={`btn-available-${o.id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1"/> Disponible
                            </Button>
                            <Button size="sm" onClick={() => set(o.id, { status: "out_of_stock", note: "" }, "Marquée épuisée")} className="pill-btn bg-red-600 hover:bg-red-700 rounded-full h-9 text-xs font-bold" data-testid={`btn-oos-${o.id}`}>
                              <XCircle className="w-4 h-4 mr-1"/> Épuisé
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setSelected(o.id)} className="rounded-full h-9 text-xs" data-testid={`btn-delayed-${o.id}`}>
                              <CalendarClock className="w-4 h-4 mr-1"/> Disponible plus tard
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>🔔 Clients en attente d'alerte "Retour en stock" ({alerts.length})</CardTitle></CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-sm text-[#4B5563] py-4 text-center">Aucune personne en liste d'attente pour l'instant.</p>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const grouped = {};
                    for (const a of alerts) {
                      const k = a.product_id;
                      if (!grouped[k]) grouped[k] = { product_id: k, product_title: a.product_title, list: [] };
                      grouped[k].list.push(a);
                    }
                    return Object.values(grouped).map((g) => (
                      <div key={g.product_id} className="border border-[#1A3322]/10 rounded-lg p-4">
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <p className="font-bold">{g.product_title}</p>
                            <p className="text-xs text-[#4B5563]">{g.list.length} personne(s) en attente</p>
                          </div>
                          <Button size="sm" onClick={() => notifyProduct(g.product_id)} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-9 text-xs font-bold shrink-0" data-testid={`notify-btn-${g.product_id}`}>
                            <Bell className="w-4 h-4 mr-1"/> Envoyer l'alerte à tous
                          </Button>
                        </div>
                        <div className="mt-2 text-xs text-[#4B5563] flex flex-wrap gap-x-3 gap-y-1">
                          {g.list.slice(0, 5).map((a) => <span key={a.id}>{a.email}</span>)}
                          {g.list.length > 5 && <span>+ {g.list.length - 5} autres</span>}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function DelayedForm({ order, onCancel, onSubmit }) {
  const [days, setDays] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("days"); // days | date

  const submit = () => {
    if (mode === "days") {
      const d = parseInt(days, 10);
      if (!d || d <= 0) return;
      onSubmit(d, null, note);
    } else {
      if (!date) return;
      onSubmit(null, date, note);
    }
  };

  return (
    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg space-y-2" data-testid="delayed-form">
      <div className="flex gap-2 text-xs">
        <button type="button" onClick={() => setMode("days")} className={`px-3 py-1 rounded-full ${mode === "days" ? "bg-[#1A3322] text-white" : "bg-white border"}`}>Délai en jours</button>
        <button type="button" onClick={() => setMode("date")} className={`px-3 py-1 rounded-full ${mode === "date" ? "bg-[#1A3322] text-white" : "bg-white border"}`}>Date précise</button>
      </div>
      {mode === "days" ? (
        <div>
          <Label className="text-xs">Disponible dans (jours)</Label>
          <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} placeholder="Ex: 7"/>
        </div>
      ) : (
        <div>
          <Label className="text-xs">Date de disponibilité</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}/>
        </div>
      )}
      <div>
        <Label className="text-xs">Message au client (facultatif)</Label>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Nouveau restock prévu la semaine prochaine"/>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} className="pill-btn bg-yellow-600 hover:bg-yellow-700 rounded-full h-9 text-xs font-bold" data-testid="delayed-submit">
          Confirmer et notifier
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-full h-9 text-xs">Annuler</Button>
      </div>
    </div>
  );
}
